import { ScheduledJob } from "@/types/common.types";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  VisibilityState,
  SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState, useCallback } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerTable } from "../../common/Table";
import Text from "../../common/Text";
import { format, subMonths } from "date-fns";
import { InsightByDate } from "@/types/insights.types";
import {
  useGetInsightsForJobByPopularStyleQuery,
  useGetInsightsForJobByTrendingStyleQuery,
  useGetInsightsForJobByDateQuery,
} from "@/hooks/queries";
import { VirtualTable } from "../../common/VirtualTable";
import { ArrowUpDownIcon } from "lucide-react";

const INSIGHT_DETAIL_FOR_JOB_BY_DATE_TABLE_COLUMNS_LABELS = {
  styleId: "Style ID",
  predictedUnitSales: "Predicted Daily Units",
  predictedRevenue: "Predicted Daily Revenue",
  brand: "Brand",
  article: "Article",
  colour: "Color",
  occasion: "Occasion",
  fabric: "Fabric",
  fabric2: "Fabric 2",
  materialAndCare: "Material & Care",
  fit: "Fit",
  mrp: "MRP",
  availableCount: "Available Count",
  averageSellingPrice: "Avg. Selling Price",
  averageDiscountPercentage: "Avg. Discount %",
  rating: "Rating",
  season: "Season",
  launchYear: "Launch Year",
};

type JobsDataTableProps = {
  selectedOptions?: string;
  job: ScheduledJob;
  date: string;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearchValue: string;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
};

type InsightData = {
  pages: Array<{
    data: InsightByDate[];
    totalElements: number;
  }>;
};

// Function to get date 1 month ago in yyyy-MM-dd format
const getOneMonthAgoDate = (): string => {
  const currentDate = new Date();
  const oneMonthAgo = subMonths(currentDate, 1);
  return format(oneMonthAgo, "yyyy-MM-dd");
};
const getTwoMonthAgoDate = (): string => {
  const currentDate = new Date();
  const oneMonthAgo = subMonths(currentDate, 2);
  return format(oneMonthAgo, "yyyy-MM-dd");
};
const getThreeMonthAgoDate = (): string => {
  const currentDate = new Date();
  const oneMonthAgo = subMonths(currentDate, 3);
  return format(oneMonthAgo, "yyyy-MM-dd");
};

const InsightDetailForJobByDateTable: React.FC<JobsDataTableProps> = ({
  selectedOptions,
  job,
  date,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  // Invoke all hooks unconditionally
  const popularStyleQuery = useGetInsightsForJobByPopularStyleQuery(
    {
      jobId: job.id.toString(),
      date: date,
    },
    {
      enabled: selectedOptions === "popularStyle",
    }
  );

  const trendingStyle1MonthQuery = useGetInsightsForJobByTrendingStyleQuery(
    {
      jobId: job.id.toString(),
      date: date,
      trendDate: getOneMonthAgoDate(),
    },
    {
      enabled: selectedOptions === "trendingStyle1month",
    }
  );

  const trendingStyle2MonthQuery = useGetInsightsForJobByTrendingStyleQuery(
    {
      jobId: job.id.toString(),
      date: date,
      trendDate: getTwoMonthAgoDate(),
    },
    {
      enabled: selectedOptions === "trendingStyle2month",
    }
  );

  const trendingStyle3MonthQuery = useGetInsightsForJobByTrendingStyleQuery(
    {
      jobId: job.id.toString(),
      date: date,
      trendDate: getThreeMonthAgoDate(),
    },
    {
      enabled: selectedOptions === "trendingStyle3month",
    }
  );

  const defaultQuery = useGetInsightsForJobByDateQuery(
    {
      jobId: job.id.toString(),
      date: date,
    },
    50 // page size
  );

  // Select the appropriate query data based on option
  const data = useMemo<InsightData>(() => {
    if (selectedOptions === "popularStyle")
      return {
        pages: [
          {
            data: popularStyleQuery.data || [],
            totalElements: popularStyleQuery.data?.length || 0,
          },
        ],
      };
    if (selectedOptions === "trendingStyle1month")
      return {
        pages: [
          {
            data: trendingStyle1MonthQuery.data || [],
            totalElements: trendingStyle1MonthQuery.data?.length || 0,
          },
        ],
      };
    if (selectedOptions === "trendingStyle2month")
      return {
        pages: [
          {
            data: trendingStyle2MonthQuery.data || [],
            totalElements: trendingStyle2MonthQuery.data?.length || 0,
          },
        ],
      };
    if (selectedOptions === "trendingStyle3month")
      return {
        pages: [
          {
            data: trendingStyle3MonthQuery.data || [],
            totalElements: trendingStyle3MonthQuery.data?.length || 0,
          },
        ],
      };
    return {
      pages: defaultQuery.data?.pages || [],
    };
  }, [
    selectedOptions,
    popularStyleQuery.data,
    trendingStyle1MonthQuery.data,
    trendingStyle2MonthQuery.data,
    trendingStyle3MonthQuery.data,
    defaultQuery.data,
  ]);

  // Combine loading/error states
  const isLoading = useMemo(() => {
    if (selectedOptions === "popularStyle") return popularStyleQuery.isLoading;
    if (selectedOptions === "trendingStyle1month")
      return trendingStyle1MonthQuery.isLoading;
    if (selectedOptions === "trendingStyle2month")
      return trendingStyle2MonthQuery.isLoading;
    if (selectedOptions === "trendingStyle3month")
      return trendingStyle3MonthQuery.isLoading;
    return defaultQuery.isLoading;
  }, [
    selectedOptions,
    popularStyleQuery.isLoading,
    trendingStyle1MonthQuery.isLoading,
    trendingStyle2MonthQuery.isLoading,
    trendingStyle3MonthQuery.isLoading,
    defaultQuery.isLoading,
  ]);

  const isFetchingNextPage = useMemo(() => {
    if (selectedOptions === "popularStyle") return false;
    if (selectedOptions === "trendingStyle1month") return false;
    if (selectedOptions === "trendingStyle2month") return false;
    if (selectedOptions === "trendingStyle3month") return false;
    return defaultQuery.isFetchingNextPage;
  }, [selectedOptions, defaultQuery.isFetchingNextPage]);

  const hasNextPage = useMemo(() => {
    if (selectedOptions === "popularStyle") return false;
    if (selectedOptions === "trendingStyle1month") return false;
    if (selectedOptions === "trendingStyle2month") return false;
    if (selectedOptions === "trendingStyle3month") return false;
    return defaultQuery.hasNextPage;
  }, [selectedOptions, defaultQuery.hasNextPage]);

  const fetchNextPage = useCallback(() => {
    if (selectedOptions === "popularStyle") return;
    if (selectedOptions === "trendingStyle1month") return;
    if (selectedOptions === "trendingStyle2month") return;
    if (selectedOptions === "trendingStyle3month") return;
    defaultQuery.fetchNextPage();
  }, [selectedOptions, defaultQuery.fetchNextPage]);

  const isError = useMemo(() => {
    if (selectedOptions === "popularStyle") return popularStyleQuery.isError;
    if (selectedOptions === "trendingStyle1month")
      return trendingStyle1MonthQuery.isError;
    if (selectedOptions === "trendingStyle2month")
      return trendingStyle2MonthQuery.isError;
    if (selectedOptions === "trendingStyle3month")
      return trendingStyle3MonthQuery.isError;
    return defaultQuery.isError;
  }, [
    selectedOptions,
    popularStyleQuery.isError,
    trendingStyle1MonthQuery.isError,
    trendingStyle2MonthQuery.isError,
    trendingStyle3MonthQuery.isError,
    defaultQuery.isError,
  ]);

  const error = useMemo(() => {
    if (selectedOptions === "popularStyle") return popularStyleQuery.error;
    if (selectedOptions === "trendingStyle1month")
      return trendingStyle1MonthQuery.error;
    if (selectedOptions === "trendingStyle2month")
      return trendingStyle2MonthQuery.error;
    if (selectedOptions === "trendingStyle3month")
      return trendingStyle3MonthQuery.error;
    return defaultQuery.error;
  }, [
    selectedOptions,
    popularStyleQuery.error,
    trendingStyle1MonthQuery.error,
    trendingStyle2MonthQuery.error,
    trendingStyle3MonthQuery.error,
    defaultQuery.error,
  ]);

  const tableData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  const insightDataColumns: ColumnDef<InsightByDate>[] = useMemo(
    () => [
      {
        accessorKey: "styleId",
        header: "Style ID",
        cell: ({ row }) => (
          <div className="cursor-pointer text-left capitalize text-brand-accent underline">
            {row.original.productLink ? (
              <a
                href={row.original.productLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.styleId || "-"}
              </a>
            ) : (
              row.original.styleId || "-"
            )}
          </div>
        ),
      },
      {
        accessorKey: "imageLink",

        header: "Image",
        cell: ({ row }) => (
          <div className="text-left">
            {row.original.imageLink ? (
              <img
                src={row.original.imageLink}
                alt={row.original.styleId || "Product image"}
                className="h-14 w-16 rounded-none object-cover"
              />
            ) : (
              <div className="h-14 w-16 rounded-none bg-gray-100" />
            )}
          </div>
        ),
      },
      {
        accessorKey: "predictedUnitSales",
        header: ({ column }) => (
          <div
            className="flex cursor-pointer items-center justify-start gap-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="ml-2 h-4 w-4" />
            Predicted Daily Units
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.predictedUnitSales || "-"}
          </div>
        ),
      },
      {
        accessorKey: "predictedRevenue",
        header: ({ column }) => (
          <div
            className="flex cursor-pointer items-center justify-start gap-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="ml-2 h-4 w-4" />
            Predicted Daily Revenue
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.predictedRevenue || "-"}
          </div>
        ),
      },
      {
        accessorKey: "brand",
        header: "Brand",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.brand || "-"}
          </div>
        ),
      },
      {
        accessorKey: "article",
        header: "Article",
        cell: ({ row }) => (
          <div className="text-left">{row.original.article || "-"}</div>
        ),
      },
      {
        accessorKey: "colour",
        header: "Color",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.colour || "-"}
          </div>
        ),
      },
      {
        accessorKey: "occasion",
        header: "Occasion",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.occasion || "-"}
          </div>
        ),
      },
      {
        accessorKey: "mrp",
        header: "MRP",
        cell: ({ row }) => (
          <div className="text-left">{row.original.mrp || "-"}</div>
        ),
      },
      {
        accessorKey: "averageSellingPrice",
        header: "Avg. Selling Price",
        cell: ({ row }) => (
          <div className="text-left">
            {row.original.averageSellingPrice || "-"}
          </div>
        ),
      },
      {
        accessorKey: "averageDiscountPercentage",
        header: "Avg. Discount %",
        cell: ({ row }) => (
          <div className="text-left">
            {row.original.averageDiscountPercentage
              ? `${row.original.averageDiscountPercentage}%`
              : "-"}
          </div>
        ),
      },
      {
        accessorKey: "rating",
        header: "Rating",
        cell: ({ row }) => (
          <div className="text-left">{row.original.rating || "-"}</div>
        ),
      },
      {
        accessorKey: "season",
        header: "Season",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.season || "-"}
          </div>
        ),
      },
      {
        accessorKey: "launchYear",
        header: "Launch Year",
        cell: ({ row }) => (
          <div className="text-left">{row.original.launchYear || "-"}</div>
        ),
      },
      {
        accessorKey: "fabric",
        header: "Fabric",
        cell: ({ row }) => (
          <div className="text-center capitalize">
            {row.original.fabric || "-"}
          </div>
        ),
      },
      {
        accessorKey: "fabric2",
        header: "Fabric 2",
        cell: ({ row }) => (
          <div className="text-center capitalize">
            {row.original.fabric2 || "-"}
          </div>
        ),
      },
      {
        accessorKey: "materialAndCare",
        header: "Material & Care",
        cell: ({ row }) => (
          <div className="text-center capitalize">
            {row.original.materialAndCare || "-"}
          </div>
        ),
      },
      {
        accessorKey: "fit",
        header: "Fit",
        cell: ({ row }) => (
          <div className="text-center capitalize">
            {row.original.fit || "-"}
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns: insightDataColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  });

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            INSIGHT_DETAIL_FOR_JOB_BY_DATE_TABLE_COLUMNS_LABELS[
              column.id as keyof typeof INSIGHT_DETAIL_FOR_JOB_BY_DATE_TABLE_COLUMNS_LABELS
            ] || column.id,
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);
  if (isLoading) {
    return (
      <JobDataHeader totalJobs={0}>
        <ShimmerTable<InsightByDate>
          columns={insightDataColumns}
          rowCount={10}
        />
      </JobDataHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobDataHeader totalJobs={data?.pages?.[0]?.totalElements || 0}>
        <VirtualTable
          table={table}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          columns={insightDataColumns}
        />
      </JobDataHeader>
    </>
  );
};

export default InsightDetailForJobByDateTable;

type JobDataHeaderProps = {
  totalJobs?: number;
  children: React.ReactNode;
  table?: ReturnType<typeof useReactTable<InsightByDate>>;
};

const JobDataHeader: React.FC<JobDataHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs && totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
